import { useFeatureGate } from '@statsig/react-bindings';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LOCALE_DEFAULT, useI18nReact, useTranslate } from '@growtherapy/i18n';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@growtherapy/sprout-ui';

import { logger } from '../datadog/logger';
import { ADDITIONAL_SUPPORTED_LOCALES } from '../i18n/config';
import {
  getPreferredSupportedLocale,
  hasStoredLanguagePreference,
  setStoredLanguagePreference,
} from '../i18n/languagePreference';
import { useTrackEvent } from '../segment/segment';
import { EventTypeEnum } from '../segment/types';
import { localizationTelehealthClientPromptGate } from '../statsig/gates';
import { normalizeError } from '../utils';
import { LOCALE_DETECTION_PROMPT_CONFIG, LocaleDetectionPromptConfig } from './browserLanguagePromptConfig';

type BrowserLanguagePromptModalProps = {
  isOpen: boolean;
  config: LocaleDetectionPromptConfig;
  onKeepEnglish: () => void;
  onSwitchLanguage: () => void;
};

export function BrowserLanguagePromptModal({
  isOpen,
  config,
  onKeepEnglish,
  onSwitchLanguage,
}: BrowserLanguagePromptModalProps) {
  const t = useTranslate();

  return (
    <Modal isOpen={isOpen} onClose={onKeepEnglish} className='rebrand:rounded-none sm:!top-1/2 sm:!-translate-y-1/2'>
      <ModalHeader>{config.headerText}</ModalHeader>
      <ModalBody>{config.bodyText}</ModalBody>
      <ModalFooter
        actions={[
          {
            key: 'keep-english',
            use: 'secondary',
            onClick: onKeepEnglish,
            actionContent: t('telehealth-core:settings.keepEnglish'),
          },
          {
            key: 'switch-language',
            use: 'primary',
            onClick: onSwitchLanguage,
            actionContent: config.actionCta,
          },
        ]}
      />
    </Modal>
  );
}

export function BrowserLanguagePrompt() {
  const i18n = useI18nReact();
  const { userClicked, userViewed } = useTrackEvent();
  const hasTrackedViewRef = useRef(false);

  const [detectedLocale] = useState(() => {
    // If a language preference is already stored, don't show the prompt
    if (hasStoredLanguagePreference()) return null;
    const preferredSupportedLocale = getPreferredSupportedLocale(ADDITIONAL_SUPPORTED_LOCALES);
    // If the preferred supported locale is the same as the current language, don't show the prompt
    if (preferredSupportedLocale === i18n.language) return null;
    return preferredSupportedLocale;
  });

  const config = detectedLocale ? LOCALE_DETECTION_PROMPT_CONFIG[detectedLocale] : undefined;

  // If there is no detected locale with a config, don't open the modal
  const [isOpen, setIsOpen] = useState(!!config);

  const handleKeepEnglish = useCallback(() => {
    userClicked(EventTypeEnum.BROWSER_LANG_NOTICE_MODAL_KEEP, { suggested: detectedLocale! });
    setStoredLanguagePreference(LOCALE_DEFAULT);
    setIsOpen(false);
  }, [userClicked, detectedLocale]);

  const handleSwitchLanguage = useCallback(async () => {
    try {
      userClicked(EventTypeEnum.BROWSER_LANG_NOTICE_MODAL_SWITCH, {
        previous: i18n.language,
        current: detectedLocale!,
      });
      await i18n.changeLanguage(detectedLocale!);
      setStoredLanguagePreference(detectedLocale!);
    } catch (error) {
      logger.error(
        'Failed to change language from browser language prompt',
        { detectedLocale, previousLanguage: i18n.language },
        normalizeError(error),
      );
      return;
    }
    setIsOpen(false);
  }, [detectedLocale, i18n, userClicked]);

  useEffect(
    function fireBrowserLangModalViewedEvent() {
      if (isOpen && detectedLocale && !hasTrackedViewRef.current) {
        hasTrackedViewRef.current = true;
        userViewed(EventTypeEnum.BROWSER_LANG_NOTICE_MODAL_VIEWED, { suggested: detectedLocale });
      }
    },
    [isOpen, detectedLocale, userViewed],
  );

  if (!config || !isOpen) return null;

  return (
    <BrowserLanguagePromptModal
      isOpen={isOpen}
      config={config}
      onKeepEnglish={handleKeepEnglish}
      onSwitchLanguage={handleSwitchLanguage}
    />
  );
}

export function BrowserLanguagePromptWrapper() {
  const { value: isEnabled } = useFeatureGate(localizationTelehealthClientPromptGate);
  if (!isEnabled) return null;
  return <BrowserLanguagePrompt />;
}
